import { useMemo } from 'react';
import { formatDateTime, DATE_FORMATS, template } from '@belong/common';
import { IconBack } from '@belong/icons';
import { Text, ButtonBase } from '@belong/ui';
import clsx from 'clsx';
import { ApplicationDetails } from 'src/api/models';
import { GET_STARTED_STRINGS } from '../get-started.strings';

type ApplicationProps = {
  application: ApplicationDetails;
  onPress: (application: ApplicationDetails) => void;
  userEmail: string;
  className?: string;
};

function Application({
  application,
  onPress,
  userEmail,
  className,
}: ApplicationProps) {
  const appliedWith = useMemo(
    () =>
      [application.applicants?.mainApplicant]
        .concat(application.applicants?.coApplicants)
        .filter((applicant) => applicant?.email !== userEmail)
        .map((applicant) => `${applicant?.firstName} ${applicant?.lastName}`),
    [
      application.applicants?.coApplicants,
      application.applicants?.mainApplicant,
      userEmail,
    ],
  );
  function handleSelect() {
    onPress(application);
  }

  return (
    <ButtonBase
      type="button"
      key={application.basicInfo?.applicationId}
      className={clsx(
        'border-gray p-sm hover:border-navy w-full cursor-pointer rounded border border-solid',
        className,
      )}
      onClick={handleSelect}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-col items-start text-left">
          <div>
            {application.basicInfo?.createdOn
              ? template(GET_STARTED_STRINGS.existing_applications.created_on, {
                  date: formatDateTime({
                    dateTime: application.basicInfo?.createdOn,
                    format: DATE_FORMATS.DAY_MONTH_YEAR,
                  }),
                })
              : `Application #${application.basicInfo?.applicationId}`}
          </div>
          {appliedWith.length > 0 && (
            <div className="pr-sm flex max-w-full justify-start truncate">
              <Text variant="p1" className="truncate">
                {GET_STARTED_STRINGS.existing_applications.apply_with}
                {appliedWith.map((applicantFullName, index) => (
                  <span className="ml-2xs font-semibold" key={index}>
                    {applicantFullName}
                  </span>
                ))}
              </Text>
            </div>
          )}
        </div>
        <div className="gap-md flex items-center">
          <IconBack width={15} className="rotate-180" />
        </div>
      </div>
    </ButtonBase>
  );
}

type ExistingApplicationsProps = {
  selectedApplication?: ApplicationDetails;
  onApplicationPress: (selectedApplication: ApplicationDetails) => void;
  applications: ApplicationDetails[];
  userEmail: string;
  className?: string;
};

export function ExistingApplications({
  onApplicationPress,
  applications,
  userEmail,
  className,
}: ExistingApplicationsProps) {
  return (
    <div className={className}>
      <Text
        fontWeight="semibold"
        className="mb-sm text-h3-sm md:text-h3-xl text-center"
      >
        {applications.length > 1
          ? GET_STARTED_STRINGS.existing_applications.title_multiple
          : GET_STARTED_STRINGS.existing_applications.title_one}
      </Text>
      {applications.map((application, index) => (
        <Application
          key={index}
          application={application}
          onPress={onApplicationPress}
          userEmail={userEmail}
          className={applications.length - 1 !== index ? 'mb-xs' : ''}
        />
      ))}
    </div>
  );
}
